import type { FC, ReactNode } from 'react'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'
import { clamp } from '@/lib/motion/math'

const INK = [29, 42, 49]
const PALE = [198, 205, 208]
const HAZE = [154, 163, 168]

interface BracketProps {
    children: ReactNode
    className?: string
}

/** Tiny monospace uppercase label wrapped in square brackets, like [ MAX RANGE ]. */
export const Bracket: FC<BracketProps> = ({ children, className }): ReactNode => {
    return (
        <span className={cn('label-mono inline-flex items-center gap-[0.6em] whitespace-nowrap', className)}>
            <span aria-hidden='true'>[</span>
            <span>{children}</span>
            <span aria-hidden='true'>]</span>
        </span>
    )
}

interface LogoMarkProps {
    className?: string
}

export const LogoMark: FC<LogoMarkProps> = ({ className }): ReactNode => {
    return (
        <svg viewBox='0 0 24 24' fill='none' aria-hidden='true' className={className}>
            <circle cx='12' cy='12' r='7.25' stroke='currentColor' strokeWidth='1.6' />
            <path d='M1.5 12h3.2M19.3 12h3.2' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
            <circle cx='12' cy='12' r='2.6' fill='#8fe3f0' />
        </svg>
    )
}

interface RevealWordsProps {
    text: string
    className?: string
    wordClassName?: string
}

/** Splits copy into words so they can be revealed one by one from grey to ink. */
export const RevealWords: FC<RevealWordsProps> = ({ text, className, wordClassName }): ReactNode => {
    const words = text.split(' ')

    return (
        <span className={className}>
            {words.map((word, index) => (
                <Fragment key={`${word}-${index}`}>
                    <span data-reveal-word='' className={cn('reveal-word', wordClassName)}>
                        {word}
                    </span>
                    {index < words.length - 1 ? ' ' : null}
                </Fragment>
            ))}
        </span>
    )
}

interface MaskTitleProps {
    children: ReactNode
    className?: string
    innerClassName?: string
}

/** A line that slides up from behind a mask. Drive `.mask-inner` with translateY. */
export const MaskTitle: FC<MaskTitleProps> = ({ children, className, innerClassName }): ReactNode => {
    return (
        <span className={cn('block overflow-hidden pb-[0.06em]', className)}>
            <span data-mask-inner='' className={cn('mask-inner block will-change-transform', innerClassName)}>
                {children}
            </span>
        </span>
    )
}

type RevealMode = 'ink' | 'fade'

/**
 * Returns an updater that colours words from pale grey to ink (or fades grey words in)
 * according to a 0..1 progress. Only touches the DOM when a word actually changes.
 */
export const createWordReveal = (root: HTMLElement | null, mode: RevealMode = 'ink', softness = 2.4): ((progress: number) => void) => {
    const words = root ? Array.from(root.querySelectorAll<HTMLElement>('[data-reveal-word]')) : []
    const last = new Float32Array(words.length).fill(-1)

    return (progress: number) => {
        const head = clamp(progress) * (words.length + softness)

        for (let i = 0; i < words.length; i++) {
            const t = Math.round(clamp((head - i) / softness) * 100) / 100

            if (t === last[i]) continue

            last[i] = t

            if (mode === 'ink') {
                const r = Math.round(PALE[0] + (INK[0] - PALE[0]) * t)
                const g = Math.round(PALE[1] + (INK[1] - PALE[1]) * t)
                const b = Math.round(PALE[2] + (INK[2] - PALE[2]) * t)

                words[i].style.color = `rgb(${r}, ${g}, ${b})`
            }
            else {
                words[i].style.color = `rgb(${HAZE[0]}, ${HAZE[1]}, ${HAZE[2]})`
                words[i].style.opacity = String(0.08 + 0.92 * t)
            }
        }
    }
}

/** Drives every `.mask-inner` inside root: 0 hidden below the mask, 1 fully in place. */
export const createMaskDriver = (root: HTMLElement | null, stagger = 0.12): ((progress: number) => void) => {
    const lines = root ? Array.from(root.querySelectorAll<HTMLElement>('[data-mask-inner]')) : []
    const last = new Float32Array(lines.length).fill(-1)

    return (progress: number) => {
        for (let i = 0; i < lines.length; i++) {
            const local = clamp((progress - i * stagger) / Math.max(0.0001, 1 - (lines.length - 1) * stagger))
            const eased = 1 - Math.pow(1 - local, 3)
            const value = Math.round(eased * 1000) / 1000

            if (value === last[i]) continue

            last[i] = value
            lines[i].style.transform = `translate3d(0, ${(1 - value) * 105}%, 0)`
        }
    }
}

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%+/<>'

/** Scrambles an element's text briefly before resolving to `text`, left to right. */
export const scrambleText = (element: HTMLElement | null, text: string, duration = 520): (() => void) => {
    if (!element) return () => undefined

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reduce) {
        element.textContent = text

        return () => undefined
    }

    const from = element.textContent ?? ''
    const length = Math.max(from.length, text.length)
    const start = performance.now()

    let frame = 0

    const tick = (now: number): void => {
        const t = Math.min(1, (now - start) / duration)
        let output = ''

        for (let i = 0; i < length; i++) {
            const char = text[i]
            const settle = 0.25 + (i / Math.max(1, length)) * 0.7

            if (t >= settle) output += char ?? ''
            else if (char === ' ') output += ' '
            else output += GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
        }

        element.textContent = t >= 1 ? text : output

        if (t < 1) frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(frame)
}

/** Sets a style property only when its value changed since the last call. */
export const createStyleCache = () => {
    const cache = new WeakMap<HTMLElement | SVGElement, Map<string, string>>()

    return (element: HTMLElement | SVGElement | null | undefined, property: string, value: string): void => {
        if (!element) return

        let entry = cache.get(element)

        if (!entry) {
            entry = new Map()
            cache.set(element, entry)
        }

        if (entry.get(property) === value) return

        entry.set(property, value)
        element.style.setProperty(property, value)
    }
}
